import React, { useState } from "react";
import LegalLayout from "@/components/LegalLayout";
import { Envelope, PaperPlaneRight, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/**
 * Contact / Kontakt
 *
 * Big mailto button + a working contact form. The form posts to
 * POST /api/contact and the backend either emails support@teamfit.dk via
 * Resend (when configured) or stores the request in a `contact_messages`
 * collection so nothing is ever lost.
 */
export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(lang === "da" ? "Udfyld alle felter" : "Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success(lang === "da" ? "Tak — vi vender tilbage hurtigst muligt." : "Thanks — we'll get back to you shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || (lang === "da" ? "Noget gik galt. Prøv igen." : "Something went wrong. Please try again."));
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalLayout kicker={t("legal.contact_kicker")} title={t("legal.contact_title")}>
      <p>
        {lang === "da"
          ? "Skriv direkte til os på "
          : "Reach us directly at "}
        <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>
        {lang === "da" ? " — eller brug formularen herunder." : " — or use the form below."}
      </p>

      <div className="not-prose mt-6">
        <a
          href="mailto:support@teamfit.dk"
          data-testid="contact-mailto"
          className="kinetic-btn inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#0a66d0] text-white px-5 py-3 text-sm font-bold uppercase tracking-widest"
        >
          <Envelope weight="bold" size={16} />
          {lang === "da" ? "Skriv en mail" : "Send an email"}
        </a>
      </div>

      <hr className="my-10" />

      <h2>{lang === "da" ? "Eller send en besked her" : "Or send a message here"}</h2>

      {sent ? (
        <div
          className="not-prose flex items-start gap-3 p-5 border"
          style={{ borderColor: "var(--tf-volt)", background: "rgba(52,199,89,0.08)" }}
          data-testid="contact-sent-banner"
        >
          <Check weight="bold" size={20} color="#34C759" />
          <div>
            <div className="font-display text-lg tracking-tight">
              {lang === "da" ? "Modtaget!" : "Got it!"}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--tf-text-2)" }}>
              {lang === "da"
                ? "Vi svarer på support@teamfit.dk inden for 1–2 hverdage."
                : "We'll reply from support@teamfit.dk within 1–2 business days."}
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="not-prose space-y-4 mt-6">
          <Field
            label={lang === "da" ? "Dit navn" : "Your name"}
            testId="contact-name"
            value={form.name}
            onChange={(v) => setForm((f) => ({ ...f, name: v }))}
            type="text"
          />
          <Field
            label={lang === "da" ? "Din e-mail" : "Your email"}
            testId="contact-email"
            value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            type="email"
          />
          <div>
            <label className="label-overline block mb-2">
              {lang === "da" ? "Besked" : "Message"}
            </label>
            <textarea
              data-testid="contact-message"
              rows={6}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="w-full px-4 py-3 text-sm border focus:outline-none focus:border-[#007AFF]"
              style={{
                background: "var(--tf-surface-2)",
                borderColor: "var(--tf-border)",
                color: "var(--tf-text)",
                resize: "vertical",
              }}
              placeholder={lang === "da" ? "Skriv din besked her…" : "Type your message here…"}
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="kinetic-btn inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-60 text-white px-5 py-3 text-sm font-bold uppercase tracking-widest"
          >
            <PaperPlaneRight weight="bold" size={16} />
            {sending
              ? (lang === "da" ? "Sender…" : "Sending…")
              : (lang === "da" ? "Send besked" : "Send message")}
          </button>
        </form>
      )}
    </LegalLayout>
  );
}

function Field({ label, value, onChange, type, testId }) {
  return (
    <div>
      <label className="label-overline block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 text-sm border focus:outline-none focus:border-[#007AFF]"
        style={{
          background: "var(--tf-surface-2)",
          borderColor: "var(--tf-border)",
          color: "var(--tf-text)",
        }}
      />
    </div>
  );
}
